/** @jsx jsx */
import { css, jsx } from "@emotion/core"
import React from "react"
import { Icon, Spacer } from "@cd2/components"
import { P } from "@cd2/text/build/es6"
import { useTheme } from "@cd2/theme/build/es6"
import { FieldWithDescription } from "./FieldWithDescription"
import { colors } from "./EOSThemeProvider"
import { useField } from "@cd2/forms/build/es6/Form2"
import { Form2 } from "@cd2/forms"
import { QuickToolTipWrapper } from "./QuickToolTipWrapper"
import { icons } from "../constants/icons"

export function FieldsRadioCheckbox({
  label,
  value,
  onChange,
  onClick,
  error,
  description,
  disabled,
  name = "radio",
  type = "radio",
}: {
  label?: React.ReactNode
  value: boolean
  onChange?(value: boolean): void
  onClick?(): void
  error?: string
  description?: string
  disabled?: boolean
  name?: string
  type?: "radio" | "checkbox"
}) {
  const theme = useTheme()

  function handleChange(value: boolean) {
    if (disabled) {
      return
    }

    onChange?.(value)
  }

  return (
    <label>
      <div
        css={css`
          border: 1px solid transparent;
          padding: 8px;
          border-radius: 5px;
          margin: -1px;
          transition: 0.2s;
          cursor: ${disabled ? "not-allowed" : "pointer"};
          opacity: ${disabled ? 0.5 : 1};
          ${!disabled &&
          `
            :hover {
              border-color: ${colors.lightBorder};
            }
            :active {
              background: #f1f1f1;
            }
          `}
        `}
      >
        <FieldWithDescription description={description} checkbox>
          <div
            css={css`
              display: flex;
              align-items: center;
            `}
          >
            <input
              css={css`
                width: 16px;
              `}
              type={type}
              name={name}
              onChange={e => handleChange(e.target.checked)}
              onClick={onClick}
              checked={value}
            />
            <Spacer size={4} horizontal />
            <span
              css={css`
                flex: 1;
                font-weight: ${theme.forms.label?.font?.weight};
                font-size: ${theme.forms.label?.font?.size}px;
                color: ${theme.forms.label?.font?.color};
              `}
            >
              {label}
            </span>
          </div>
        </FieldWithDescription>
        {error && <P color="red">{error}</P>}
      </div>
    </label>
  )
}

interface IRadioButtonSelectProps {
  label?: React.ReactNode
  field: string
  options: { label: string; value: string; description?: string }[]
  validations?: { presence?: boolean }
  optionDisabled?: (option: { label: string; value: string; description?: string }) => boolean
}

export function RadioButtonSelect({
  label,
  field,
  options,
  validations,
  optionDisabled,
}: IRadioButtonSelectProps) {
  const selectField = useField<string>(field, { subscribe: true })
  Form2.useFormSubmitLock(() => {
    if (validations?.presence) {
      const value = selectField.getValue()
      if (!value) {
        selectField.setError("is required")
        return false
      } else {
        selectField.setError(undefined)
      }
    }
  })
  return (
    <div>
      {label && (
        <div
          css={css`
            margin-bottom: 2px;
          `}
        >
          {typeof label === "string" ? (
            <Form2.FieldLabel label={label} required={!!validations?.presence} fieldName={field} />
          ) : (
            label
          )}
        </div>
      )}
      {options.map(({ label, value, description }) => (
        <FieldsRadioCheckbox
          key={value}
          label={label}
          value={selectField.getValue() === value}
          onChange={v => selectField.setValue(v ? value : undefined)}
          name={field}
          disabled={optionDisabled?.({ label, value })}
          description={description}
        />
      ))}
      {selectField.getError() && (
        <React.Fragment>
          <Spacer size={4} />
          <P size="small" color="error">
            {selectField.getError()}
          </P>
        </React.Fragment>
      )}
    </div>
  )
}

interface IFieldsRadioButtonSelectProps {
  onChange: (value: any) => void
  value: any
  name?: string
  label?: React.ReactNode
  options: {
    label: string
    value: any
    description?: string
    disabled?: boolean
    tooltip?: string
  }[]
  error?: string
  clearOnReselect?: boolean
}

export function FieldsRadioButtonSelect({
  name,
  options,
  label,
  onChange,
  value: selectedValue,
  error,
  clearOnReselect,
}: IFieldsRadioButtonSelectProps) {
  const theme = useTheme()
  return (
    <div
      css={css`
        label > div:first-child {
          ${error ? `border-color: ${theme.themeColor("error")};` : ""}
          :hover {
            border-color: #c5c9cd;
          }
        }
      `}
    >
      {label && (
        <div>
          {typeof label === "object" ? (
            label
          ) : (
            <label
              css={css`
                font-weight: ${theme.forms.label?.font?.weight};
                color: ${theme.forms.label?.font?.color};
              `}
            >
              {label}
            </label>
          )}
        </div>
      )}
      {options.map(({ label, value, description, disabled, tooltip }) => (
        <FieldsRadioCheckbox
          key={value}
          label={
            <QuickToolTipWrapper
              key={value}
              showToolTip={!!disabled && !!tooltip}
              content={tooltip}
            >
              {label} {disabled && tooltip ? <Icon icon={icons.help} /> : null}
            </QuickToolTipWrapper>
          }
          description={description}
          value={selectedValue === value}
          onChange={v => onChange(v ? value : undefined)}
          onClick={() => {
            if (clearOnReselect && value === selectedValue) {
              onChange(undefined)
            }
          }}
          name={name}
          disabled={disabled}
        />
      ))}
      {error && (
        <P color="error" size="small">
          {error}
        </P>
      )}
    </div>
  )
}
